/**
 * PIPELINE ED ENTITÀ — i tipi che possono avere un funnel, e il filtro per tipo.
 *
 * Stanno in un posto solo perché li usano quattro schermate: se il confronto lo scrivesse ognuna
 * per conto proprio, due copie della stessa riga divergerebbero al primo cambiamento.
 *
 * ## Perché il filtro è lato client
 *
 * C'è **un solo** slice Redux `pipelines`, con `items` sostituito a ogni fetch e letto da cinque
 * schermate (elenco in Impostazioni, creazione board, elenco board, form e dettaglio del
 * preventivo). Un filtro passato al REST finirebbe in quello slice condiviso, e l'elenco in
 * Impostazioni mostrerebbe un sottoinsieme a seconda di quale schermata ha fetchato per ultima —
 * un difetto che si manifesta solo navigando in un certo ordine. Gli elenchi per sito sono piccoli
 * e già caricati interi: filtrare qui costa niente e non ha effetti a distanza.
 */

import type { EntityTypeDescriptor } from '@crm/contracts';
import type { Pipeline } from './pipelineMappers';

/**
 * Le pipeline di un dato tipo, per FQN.
 *
 * Elenco **vuoto** se `fqn` non è valorizzato, e non l'elenco intero: chi non sa che tipo vuole non
 * sta chiedendo "tutti", sta chiedendo prima di sapere. Restituire tutto farebbe comparire funnel
 * di altre entità nella tendina, che è il difetto da cui parte TS-117.
 */
export function pipelinesOfType(pipelines: Pipeline[], fqn: string | undefined): Pipeline[] {
  if (!fqn) return [];
  return pipelines.filter((p) => p.className === fqn);
}

/**
 * I tipi proponibili quando si crea una pipeline: quelli che dichiarano la sezione `board`, cioè
 * che sanno disegnarsi in una bacheca.
 *
 * Un tipo senza `board` produrrebbe un funnel la cui bacheca non si apre — la guardia di
 * `KanbanBoardPage` — quindi non si propone: meglio non offrire una scelta che porta a un vicolo
 * cieco, che offrirla e spiegarla dopo.
 */
export function pipelineCapableTypes(types: EntityTypeDescriptor[]): EntityTypeDescriptor[] {
  return types.filter((t) => Boolean(t.board));
}
